import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import CompanyLogo from 'app/components/CommonStyled/CompanyLogo';
import { BestOf2025Icon, BookIcon } from 'app/components/shared/icons';
import { useBestOfLibrary } from 'app/features/BestOf/api';
import { singularOrPlural } from 'utils';

import {
  ResourcesFilterBarBestOfYearItemContainer,
  ResourcesFilterBarBestOfYearItemEntries,
  ResourcesFilterBarBestOfYearItemIcon,
  ResourcesFilterBarBestOfYearItemIconContainer,
  ResourcesFilterBarBestOfYearItemInformationContainer,
  ResourcesFilterBarBestOfYearItemTitle,
  ResourcesFilterBarBestOfYearSectionTitle,
  ResourcesFilterBarBestOfYearWrapper,
} from './styled';

interface ResourcesFilterBarBestOfYearProps {
  year: number;
}

export default function ResourcesFilterBarBestOfYear({ year }: ResourcesFilterBarBestOfYearProps) {
  const { push } = useHistory();
  const { pathname } = useLocation();
  const { data } = useBestOfLibrary(year);

  if (!data) return null;

  const navigate = (path: string) => () => push(path);
  const basePath = `/resources/best-of/${year}`;

  return (
    <ResourcesFilterBarBestOfYearWrapper>
      <ResourcesFilterBarBestOfYearSectionTitle>
        BEST OF {year}
      </ResourcesFilterBarBestOfYearSectionTitle>
      <ResourcesFilterBarBestOfYearItemContainer
        onClick={navigate(`${basePath}/winners`)}
        current={pathname === `${basePath}/winners`}
      >
        <BestOf2025Icon width={45} height={45} />
        <ResourcesFilterBarBestOfYearItemTitle>Winners</ResourcesFilterBarBestOfYearItemTitle>
      </ResourcesFilterBarBestOfYearItemContainer>
      {data.jury_prizes?.length ? (
        <ResourcesFilterBarBestOfYearItemContainer
          as="button"
          type="button"
          style={{ border: 0, font: 'inherit' }}
          onClick={navigate(`${basePath}/jury-prizes`)}
          current={pathname === `${basePath}/jury-prizes`}
          aria-current={pathname === `${basePath}/jury-prizes` ? 'page' : undefined}
        >
          <BestOf2025Icon width={45} height={45} />
          <ResourcesFilterBarBestOfYearItemTitle>Jury prizes</ResourcesFilterBarBestOfYearItemTitle>
        </ResourcesFilterBarBestOfYearItemContainer>
      ) : null}
      <ResourcesFilterBarBestOfYearItemContainer
        onClick={navigate(`${basePath}/all`)}
        current={pathname === `${basePath}/all`}
      >
        <BookIcon width={32} height={32} fill="var(--color-primary)" />
        <ResourcesFilterBarBestOfYearItemInformationContainer>
          <ResourcesFilterBarBestOfYearItemEntries>
            {data.totals.submissions}{' '}
            {singularOrPlural(data.totals.submissions, 'ENTRY', 'ENTRIES')}
          </ResourcesFilterBarBestOfYearItemEntries>
          <ResourcesFilterBarBestOfYearItemTitle>
            All submissions
          </ResourcesFilterBarBestOfYearItemTitle>
        </ResourcesFilterBarBestOfYearItemInformationContainer>
      </ResourcesFilterBarBestOfYearItemContainer>
      {data.my_company ? (
        <>
          <ResourcesFilterBarBestOfYearSectionTitle>
            YOUR COMPANY
          </ResourcesFilterBarBestOfYearSectionTitle>
          <ResourcesFilterBarBestOfYearItemContainer
            onClick={navigate(`${basePath}/my-company`)}
            current={pathname === `${basePath}/my-company`}
          >
            <CompanyLogo size={45} slug={data.my_company.slug} useRounded />
            <ResourcesFilterBarBestOfYearItemInformationContainer>
              <ResourcesFilterBarBestOfYearItemEntries>
                {data.my_company.submissions}{' '}
                {singularOrPlural(data.my_company.submissions, 'ENTRY', 'ENTRIES')}
              </ResourcesFilterBarBestOfYearItemEntries>
              <ResourcesFilterBarBestOfYearItemTitle>
                {data.my_company.name}
              </ResourcesFilterBarBestOfYearItemTitle>
            </ResourcesFilterBarBestOfYearItemInformationContainer>
          </ResourcesFilterBarBestOfYearItemContainer>
        </>
      ) : null}
      <ResourcesFilterBarBestOfYearSectionTitle>
        ALL CATEGORIES
      </ResourcesFilterBarBestOfYearSectionTitle>
      {data.categories.map((category) => {
        const categoryPath = `${basePath}/category/${category.slug}`;
        return (
          <ResourcesFilterBarBestOfYearItemContainer
            key={category.uuid}
            onClick={navigate(categoryPath)}
            current={pathname === categoryPath}
          >
            <ResourcesFilterBarBestOfYearItemIconContainer>
              {category.icon_url ? (
                <ResourcesFilterBarBestOfYearItemIcon
                  src={category.icon_url}
                  alt=""
                  aria-hidden="true"
                />
              ) : null}
            </ResourcesFilterBarBestOfYearItemIconContainer>
            <ResourcesFilterBarBestOfYearItemInformationContainer>
              <ResourcesFilterBarBestOfYearItemEntries>
                {category.submissions} {singularOrPlural(category.submissions, 'ENTRY', 'ENTRIES')}
              </ResourcesFilterBarBestOfYearItemEntries>
              <ResourcesFilterBarBestOfYearItemTitle>
                {category.name}
              </ResourcesFilterBarBestOfYearItemTitle>
            </ResourcesFilterBarBestOfYearItemInformationContainer>
          </ResourcesFilterBarBestOfYearItemContainer>
        );
      })}
    </ResourcesFilterBarBestOfYearWrapper>
  );
}
